import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/App";
import articles from "@/data/blogArticles";
import PUBLISHED from "@/data/blogPublished.json";

// ============================================================================
// THE POSTS ARRAY
// Add a new entry to the top of this array whenever Temple publishes a new post.
// Order = newest first. The first 3 fields are SEO-critical.
// ============================================================================
const POSTS = [
  // ===================== TEACHINGS =====================
  {
    slug: "everything-happens-for-a-reason",
    title: "\u201CEverything Happens for a Reason\u201D \u2014 Faith That Holds, Not a Silencer",
    subhead: "Faith and toxic positivity can sound identical \u2014 the difference is timing and target.",
    publishDate: "2026-08-06",
    publishDatePretty: "August 6, 2026",
    readMins: 5,
    excerpt:
      "I believe everything happens for a reason \u2014 and I will never say that sentence to a woman who is bleeding. Both are true. The same words can be sacred faith or a silencer with a spiritual accent. The difference is who says them, and when.",
    accent: "\ud83d\udd6f\ufe0f",
    color: "teal",
    keywords: ["toxic positivity", "everything happens for a reason", "grief", "witnessing", "spiritual bypassing", "good vibes only", "meaning-making"],
    series: "Teachings",
  },
  // ===================== TEMPLE'S JOURNEY SERIES (Jun 27, 2026 · latest Jul 2, 2026) =====================
  {
    slug: "the-not-so-perfect-storm",
    title: "The \u201CNot So\u201D Perfect Storm: Grief, Betrayal, and the Tools That Held Me",
    subhead: "Covid, divorce, a mother\u2019s hidden cancer, and estrangement \u2014 my soul\u2019s journey through stacked grief.",
    publishDate: "2026-07-02",
    publishDatePretty: "July 2, 2026",
    readMins: 8,
    excerpt:
      "I remember standing in complete disbelief as the world started shutting down. Covid was the storm nobody saw coming \u2014 and for me, it was only the first front. Separation, after 22 years. Then the divorce. Then grief \u2014 not one grief, but a stack of them.",
    accent: "\ud83c\udf2a\ufe0f",
    color: "deep",
    keywords: ["grief of the living", "empty-nest grief", "boundaries as codes of conduct", "forgiveness", "post-traumatic growth", "big brave work"],
    series: "Temple's Journey",
  },
  {
    slug: "i-was-told-not-to-air-our-dirty-laundry",
    title: "I Was Told Not to Air Our Dirty Laundry. Here's What I Learned Instead.",
    subhead: "The difference between exposure and turning your pain into purpose.",
    publishDate: "2026-06-28",
    publishDatePretty: "June 28, 2026",
    readMins: 6,
    excerpt:
      "\"Don't air our dirty laundry\" is rarely about privacy — it is about control of the story. Behind one door is silence. Behind the other is exposure. There is a third way, and it changes everything.",
    accent: "🧺",
    color: "deep",
    keywords: ["pain to purpose", "telling your story", "accountability", "the buck stops here", "discernment"],
    series: "Temple's Journey",
  },
  {
    slug: "when-your-spirit-stays-high-and-your-heart-is-breaking",
    title: "When Your Spirit Stays High and Your Heart Is Breaking",
    subhead: "Series finale: post-traumatic growth & the bridge.",
    publishDate: "2026-06-27",
    publishDatePretty: "June 27, 2026",
    readMins: 5,
    excerpt:
      "A broken heart and a lifted spirit are not enemies. They are roommates. You can hold devastation in one hand and hope in the other — and you are not a fraud for it. You are a whole human being.",
    accent: "🌊",
    color: "teal",
    keywords: ["post-traumatic growth", "grief", "hope", "heartbreak", "the bridge"],
    series: "Temple's Journey",
  },
  {
    slug: "coming-home-to-myself",
    title: "Coming Home to Myself",
    subhead: "The slow loss of self — and the small honest choices that brought her back.",
    publishDate: "2026-06-27",
    publishDatePretty: "June 27, 2026",
    readMins: 5,
    excerpt:
      "I caught my reflection one ordinary afternoon and did not recognize the woman looking back. Not because of the years — something behind the eyes. Somewhere along the way, I had set myself down like a bag of groceries and forgotten to pick myself back up.",
    accent: "🪞",
    color: "cream",
    keywords: ["loss of self", "reclaiming", "self-abandonment", "coming home"],
    series: "Temple's Journey",
  },
  {
    slug: "grieving-people-who-are-still-here",
    title: "Grieving People Who Are Still Here",
    subhead: "Ambiguous loss, the porch light, and grieving without going bitter.",
    publishDate: "2026-06-27",
    publishDatePretty: "June 27, 2026",
    readMins: 5,
    excerpt:
      "There is a grief no one prepares you for — the grief of missing someone who is still alive. They are out there in the world. Breathing the same air. And still, the distance between you can feel as wide as death.",
    accent: "🕯️",
    color: "deep",
    keywords: ["ambiguous loss", "estrangement", "grief", "the porch light"],
    series: "Temple's Journey",
  },
  {
    slug: "the-losses-no-one-brings-a-casserole-for",
    title: "The Losses No One Brings a Casserole For",
    subhead: "Disenfranchised grief — and why naming what was real set me free.",
    publishDate: "2026-06-27",
    publishDatePretty: "June 27, 2026",
    readMins: 5,
    excerpt:
      "When someone dies, the world knows what to do. The casseroles come. The cards arrive. But some of the heaviest losses I have ever carried came with no casserole. No card. No one standing in my kitchen saying they were sorry.",
    accent: "🍲",
    color: "teal",
    keywords: ["disenfranchised grief", "toxic positivity", "honoring loss"],
    series: "Temple's Journey",
  },
  {
    slug: "the-only-one-rowing",
    title: "I Wasn't Drowning Because I Was Weak. I Was Drowning Because I Was Rowing Alone.",
    subhead: "The marriage chapter — and why staying somewhere you cannot breathe is not strength.",
    publishDate: "2026-06-27",
    publishDatePretty: "June 27, 2026",
    readMins: 6,
    excerpt:
      "From the outside, the boat looked fine. A family. A home. A life we had built. But inside it, I didn't feel safe. I didn't feel heard. And there is a particular kind of loneliness that doesn't come from being alone — it comes from being unseen while sitting right next to someone.",
    accent: "🚣",
    color: "deep",
    keywords: ["self-abandonment", "marriage", "leaving", "choosing yourself"],
    series: "Temple's Journey",
  },
  // ===================== EARLIER POSTS =====================
  {
    slug: "you-feed-the-baby",
    title: "You Feed the Baby. Now Feed Yourself.",
    subhead: "Body hygiene — the daily check-in you'd never skip for someone you love.",
    publishDate: "2026-06-11",
    publishDatePretty: "June 11, 2026",
    readMins: 4,
    excerpt:
      "Think about how you care for a newborn. You feed her before she even cries. Nobody calls that 'self-care.' It's hygiene. So when did you stop doing that for yourself?",
    accent: "spoon",          // 🥄
    color: "teal",            // deep / cream / teal
    keywords: ["body hygiene", "self-care", "daily check-in", "nourishment", "mom self-care"],
    series: "The Body",
  },
  {
    slug: "the-messy-middle-is-the-door",
    title: "The Messy Middle Is the Door",
    subhead: "Beginning again after grief, loss, illness, and trauma.",
    publishDate: "2026-05-28",
    publishDatePretty: "May 28, 2026",
    readMins: 5,
    excerpt:
      "I had pure, authentic curiosity. And I had the fact that I was still here. The messy middle is the place between who you were and who you are becoming. I lived there. I still visit some Tuesday mornings.",
    accent: "feather",        // 🪶
    color: "deep",            // deep / cream / teal
    keywords: ["grief", "begin again", "messy middle", "rebuilding", "loss"],
  },
  // Future posts go here — newest at top.
];

export const POST_DATES = Object.fromEntries(POSTS.map((p) => [p.slug, p.publishDate]));

// Every other published post (from the article data) is listed after the curated ones.
const THEMES = ["cream", "deep", "teal"];
const ALL_POSTS = [
  ...POSTS,
  ...articles
    .filter((a) => !a.hidden && !POST_DATES[a.slug])
    .map((a, i) => ({
      slug: a.slug,
      title: a.title,
      subhead: "",
      excerpt: a.description,
      readMins: parseInt(a.readTime, 10) || null,
      accent: "feather",
      color: THEMES[i % THEMES.length],
    })),
];

// ============================================================================
// SEO
// ============================================================================
const PAGE_URL = "https://mindbodyspirithygienetools.com/blog";
const PAGE_DESC = "Essays, teachings, and notes from Temple Franklin for the woman in the messy middle. Real tools. Real women. Possessed and unperforming.";

const buildSchemas = () => [
  {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": PAGE_URL,
    name: "Mind Body Spirit HYGIENE™ Blog",
    description: PAGE_DESC,
    url: PAGE_URL,
    author: {
      "@type": "Person",
      name: "Temple Franklin",
      url: "https://mindbodyspirithygienetools.com/about",
    },
    publisher: {
      "@type": "Organization",
      name: "Mind Body Spirit HYGIENE™ Tools",
      logo: { "@type": "ImageObject", url: "https://mindbodyspirithygienetools.com/logo512.png" },
    },
    blogPost: ALL_POSTS.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.subhead || p.excerpt,
      url: `https://mindbodyspirithygienetools.com/blog/${p.slug}`,
      datePublished: p.publishDate || PUBLISHED[p.slug],
      author: { "@type": "Person", name: "Temple Franklin" },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://mindbodyspirithygienetools.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: PAGE_URL },
    ],
  },
  // ItemList helps Google understand the list of posts
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${PAGE_URL}#postlist`,
    name: "All blog posts by Temple Franklin",
    itemListElement: ALL_POSTS.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://mindbodyspirithygienetools.com/blog/${p.slug}`,
      name: p.title,
    })),
  },
];

// Icons (simple inline SVGs / emoji — matches blog post style)
const ACCENT = {
  feather: "🪶",
  book: "📓",
  spark: "✦",
  candle: "🕯",
  spoon: "🥄",
};

const COLOR_THEME = {
  deep:  { bg: "#042F2E", text: "#efeae7", accent: "#7BC8CB", chip: "#5EEAD4" },
  cream: { bg: "#ffffff", text: "#042F2E", accent: "#068fa2", chip: "#068fa2" },
  teal:  { bg: "#068fa2", text: "#efeae7", accent: "#7BC8CB", chip: "#efeae7" },
};

const BlogIndex = () => {
  useEffect(() => {
    const upsert = (key, attr, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    upsert(
      "keywords",
      "name",
      "Temple Franklin blog, Mind Body Spirit HYGIENE essays, messy middle writing, begin again midlife, grief writing, spiritual essays, daily card pull, recovering people pleaser, I get to, stop abandoning yourself"
    );

    const scripts = buildSchemas().map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-blog-index-seo", "1");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.querySelectorAll('script[data-blog-index-seo="1"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <main className="min-h-screen bg-[#efeae7]" data-testid="blog-index-page">
      <Header />

      {/* HERO */}
      <header className="pt-32 pb-16 px-6 text-center" data-testid="blog-index-hero">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-[#068fa2] font-semibold mb-6" data-testid="hero-eyebrow">
            MIND BODY SPIRIT HYGIENE™ · WRITING
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter text-[#042F2E] leading-[1.0] mb-6" data-testid="hero-title">
            The <span className="italic text-[#068fa2]">Blog.</span>
          </h1>
          <p className="font-serif italic text-lg md:text-xl text-[#1E2A29] max-w-xl mx-auto leading-relaxed" data-testid="hero-sub">
            Essays, teachings, and notes for the woman in the messy middle.
          </p>

          <div className="mt-10 mx-auto flex items-center justify-center gap-3">
            <div className="h-px w-12 bg-[#7BC8CB]/60" />
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#5F7370] font-medium" data-testid="post-count">
              {ALL_POSTS.length} {ALL_POSTS.length === 1 ? "post" : "posts"} · more landing soon
            </span>
            <div className="h-px w-12 bg-[#7BC8CB]/60" />
          </div>
        </div>
      </header>

      {/* POSTS LIST */}
      <section className="px-6 pb-16" data-testid="blog-posts-list">
        <div className="max-w-3xl mx-auto space-y-8">
          {ALL_POSTS.map((p, i) => {
            const theme = COLOR_THEME[p.color] || COLOR_THEME.deep;
            return (
              <article
                key={p.slug}
                data-testid={`blog-post-${p.slug}`}
                className="rounded-3xl shadow-sm overflow-hidden hover:shadow-xl transition-all duration-300 group"
                style={{ backgroundColor: theme.bg }}
              >
                <Link to={`/blog/${p.slug}`} className="block p-8 md:p-10">
                  {/* Date + read time chip row */}
                  <div className="flex items-center gap-4 mb-5">
                    {p.publishDatePretty && (
                      <>
                        <span
                          className="font-sans text-[10px] tracking-[0.32em] uppercase font-semibold"
                          style={{ color: theme.chip }}
                        >
                          {p.publishDatePretty}
                        </span>
                        <span className="text-xs" style={{ color: theme.accent, opacity: 0.6 }}>·</span>
                      </>
                    )}
                    <span
                      className="font-sans text-[10px] tracking-[0.32em] uppercase"
                      style={{ color: theme.accent, opacity: 0.8 }}
                    >
                      {p.readMins} MIN READ
                    </span>
                    <span className="ml-auto text-2xl" style={{ color: theme.accent }} aria-hidden="true">
                      {ACCENT[p.accent] || ACCENT.feather}
                    </span>
                  </div>

                  {/* Title */}
                  <h2
                    className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-4 group-hover:opacity-90 transition-opacity"
                    style={{ color: theme.text }}
                  >
                    {p.title}
                  </h2>

                  {/* Subhead */}
                  {p.subhead && <p
                    className="font-serif italic text-lg md:text-xl mb-5 leading-snug"
                    style={{ color: theme.text, opacity: 0.78 }}
                  >
                    {p.subhead}
                  </p>}

                  {/* Excerpt */}
                  <p
                    className="font-serif text-base md:text-[17px] leading-relaxed mb-6"
                    style={{ color: theme.text, opacity: 0.7 }}
                  >
                    {p.excerpt}
                  </p>

                  {/* Read more */}
                  <p
                    className="font-sans text-xs tracking-[0.28em] uppercase font-semibold group-hover:underline underline-offset-4"
                    style={{ color: theme.chip }}
                  >
                    Read the essay →
                  </p>
                </Link>
              </article>
            );
          })}

          {/* "More landing soon" placeholder (only shows if 1-2 posts) */}
          {POSTS.length < 3 && (
            <div className="text-center py-10 rounded-3xl border border-dashed border-[#042F2E]/15" data-testid="more-coming">
              <p className="font-serif italic text-xl text-[#5F7370] mb-2">
                More essays landing soon.
              </p>
              <p className="font-sans text-xs tracking-[0.28em] uppercase text-[#068fa2]/70 font-medium">
                Subscribe to be the first to receive them
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA — pull a card / get the printable */}
      <section className="px-6 pb-24 pt-8" data-testid="blog-cta">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#042F2E] via-[#068fa2] to-[#25a1c2] rounded-3xl p-10 md:p-14 text-center text-white">
          <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-[#5EEAD4] font-semibold mb-5">
            WHILE YOU'RE HERE
          </p>
          <h2 className="font-serif italic text-3xl md:text-4xl mb-5 leading-tight">
            Pull a card before you go.
          </h2>
          <p className="font-serif text-base md:text-lg text-white/82 mb-8 max-w-md mx-auto leading-relaxed">
            One card. One message. From the 58-card Mind Body Spirit HYGIENE™ deck. Free. Yours if it lands.
          </p>
          <Link
            to="/draw"
            data-testid="cta-pull-card"
            className="inline-block bg-white text-[#042F2E] hover:bg-[#7BC8CB] hover:text-[#042F2E] font-sans font-semibold px-8 py-4 rounded-full text-sm tracking-wider transition-colors"
          >
            🪶 Pull a card →
          </Link>
        </div>
      </section>
    </main>
  );
};

export default BlogIndex;
